import { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, Link, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useReduceMotion } from './utils/performance';
import Navbar from './components/Navbar';
import LoadingSpinner from './components/LoadingSpinner';
import { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { LogIn } from 'lucide-react';
import { requestNotificationPermission } from './utils/notifications';
import { trackPageView, trackLoadTime, trackLogin, PAGE_LOAD_START } from './services/analytics';

// Lazy load all page components for code splitting
const Landing = lazy(() => import('./pages/Landing'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const NewRequest = lazy(() => import('./pages/NewRequest'));
const MyProjects = lazy(() => import('./pages/MyProjects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const Chat = lazy(() => import('./pages/Chat'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Support = lazy(() => import('./pages/Support'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Register = lazy(() => import('./pages/Register'));
const Requests = lazy(() => import('./pages/Requests'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const MarketplaceProjectDetail = lazy(() => import('./pages/MarketplaceProjectDetail'));
const MyPurchases = lazy(() => import('./pages/MyPurchases'));

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-14 h-14 border-4 border-blue-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }
  return user ? children : <Navigate to="/login" />;
};

const LoginPage = () => {
  const { googleAuth } = useAuth();
  const reduceMotion = useReduceMotion();
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    setError('');
    try {
      const data = await googleAuth(credentialResponse.credential);
      trackLogin(data?.user);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Google sign-in failed';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google sign-in failed. Please try again.');
    toast.error('Google sign-in failed. Please try again.');
    setGoogleLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50 relative overflow-hidden">
      {/* Subtle gradient + orbs */}
      <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-amber-50/20 -z-10" />
      {!reduceMotion && (
        <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10" aria-hidden>
          <div className="absolute top-20 left-0 w-96 h-96 bg-slate-300/30 rounded-full blur-3xl animate-orb-slow" />
          <div className="absolute bottom-20 right-0 w-80 h-80 bg-amber-300/30 rounded-full blur-3xl animate-orb-slow" style={{ animationDelay: '1s' }} />
        </div>
      )}

      <motion.div
        initial={reduceMotion ? {} : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="bg-white/95 backdrop-blur border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-600 rounded-2xl shadow-lg mb-4">
              <LogIn className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Welcome Back</h2>
            <p className="text-slate-600 text-sm">Sign in with your Google account</p>
          </div>

          <div className="space-y-4">
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm"
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap={false}
              theme="outline"
              size="large"
              text="signin_with"
              shape="rectangular"
              logo_alignment="left"
              disabled={googleLoading}
              locale="en"
            />
          </div>

          <p className="mt-6 text-center text-slate-600 text-sm">
            Don't have an account?{' '}
            <Link to="/register" className="text-blue-600 font-semibold hover:underline">
              Create Account
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

const AppContent = () => {
  const location = useLocation();
  const { user } = useAuth();
  const showNavbar = !['/login', '/register', '/'].includes(location.pathname) || location.pathname.startsWith('/marketplace');

  // Track every page navigation
  useEffect(() => {
    trackPageView(location.pathname, user);
  }, [location.pathname, user]);

  return (
    <>
      {showNavbar && <Navbar />}
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<LoginPage />} />
          <Route 
            path="/register" 
            element={
              <Suspense fallback={<LoadingSpinner message="Loading registration..." />}>
                <Register />
              </Suspense>
            } 
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingSpinner message="Loading dashboard..." />}>
                  <Dashboard />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/new-request"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingSpinner message="Loading request form..." />}>
                  <NewRequest />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/my-projects"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingSpinner message="Loading projects..." />}>
                  <MyProjects />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/project/:id"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingSpinner message="Loading project details..." />}>
                  <ProjectDetail />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/chat/:projectId"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingSpinner message="Loading chat..." />}>
                  <Chat />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/requests"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingSpinner message="Loading requests..." />}>
                  <Requests />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route 
            path="/portfolio" 
            element={
              <Suspense fallback={<LoadingSpinner message="Loading portfolio..." />}>
                <Portfolio />
              </Suspense>
            } 
          />
          <Route 
            path="/support" 
            element={
              <Suspense fallback={<LoadingSpinner message="Loading support..." />}>
                <Support />
              </Suspense>
            } 
          />
          <Route 
            path="/privacy-policy" 
            element={
              <Suspense fallback={<LoadingSpinner message="Loading..." />}>
                <PrivacyPolicy />
              </Suspense>
            } 
          />
          <Route
            path="/marketplace"
            element={
              <Suspense fallback={<LoadingSpinner message="Loading marketplace..." />}>
                <Marketplace />
              </Suspense>
            }
          />
          <Route
            path="/marketplace/:id"
            element={
              <Suspense fallback={<LoadingSpinner message="Loading project..." />}>
                <MarketplaceProjectDetail />
              </Suspense>
            }
          />
          <Route
            path="/my-purchases"
            element={
              <PrivateRoute>
                <Suspense fallback={<LoadingSpinner message="Loading purchases..." />}>
                  <MyPurchases />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route path="/home" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </>
  );
};

function App() {
  useEffect(() => {
    // Request notification permission on app load
    requestNotificationPermission();

    // Track initial page load time (once per session)
    const measure = () => {
      const loadTimeMs = Date.now() - PAGE_LOAD_START;
      trackLoadTime(loadTimeMs);
    };
    if (document.readyState === 'complete') {
      measure();
    } else {
      window.addEventListener('load', measure, { once: true });
    }
  }, []);

  // Google OAuth Client ID - should be set in environment variables
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <AuthProvider>
        <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
          <div className="min-h-screen">
            <Toaster
              position="top-center"
              containerStyle={{
                top: 'max(env(safe-area-inset-top, 0px), 5.5rem)',
                zIndex: 9999,
              }}
              toastOptions={{
                duration: 3000,
                style: {
                  background: 'hsl(var(--card))',
                  color: 'hsl(var(--foreground))',
                  borderRadius: '12px',
                  boxShadow: '0 4px 24px rgba(139, 92, 246, 0.12)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                },
                success: { iconTheme: { primary: '#2563eb' } },
                error: { iconTheme: { primary: '#dc2626' } },
              }}
            />
            <AppContent />
          </div>
        </Router>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
